import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { userAtom } from "../../recoil/userAtom";
import { useRecoilValue } from "recoil";
import useIsInappWebview from "../../hooks/useIsInappWebview";
import useMixpanelWeb from "../../hooks/useMixpanelWeb";
import FullPageWithCenterContentV2 from "../container/FullPageWithCenterContentV2";
import FullPageWithHeaderAndProgress from "../container/FullPageWithHeaderAndProgress";
import { theme } from "../../styles/theme";
import useClose from "../../hooks/useClose";
import { useMutation } from "@tanstack/react-query";
import { programSetupApi } from "../../api/programSetupApi";
import { ProgramSetup_3_Load } from "./part/load/ProgramSetup_3_Load";
import Result_PS_1 from "./part/result/Result_PS_1";
import Result_PS_2 from "./part/result/Result_PS_2";
import Result_PS_3 from "./part/result/Result_PS_3";
import Result_PS_4 from "./part/result/Result_PS_4";
import PaymentContentDefault from "../../components/task/payment/PaymentContentDefault";
import Curriculum_PS_1 from "./part/curriculum/Curriculum_PS_1";
import ProductContentProgramSetup from "../../components/task/payment/ProductContentProgramSetup";
import Analysis_PS_1 from "./part/analysis/Analysis_PS_1";

interface ProgramSetupStep {
  type: string;
  seqByType: number;
  title?: string;
  hideHeader?: boolean;
  hideProgress?: boolean;
  disableBack?: boolean;
  hideBack?: boolean;
  hideClose?: boolean;
  progressDelta?: number;
  centerFullScreen?: boolean;
  backgroundColor?: string;
  paddingXContent?: number;
  content: React.ReactNode;
}

const commonSteps: ProgramSetupStep[] = [
  {
    type: "load",
    seqByType: 1,
    hideHeader: true,
    hideProgress: true,
    content: <ProgramSetup_3_Load />,
  },
  {
    type: "result",
    seqByType: 1,
    hideHeader: true,
    hideProgress: true,
    content: <Result_PS_1 />,
  },
  {
    type: "result",
    seqByType: 2,
    centerFullScreen: true,
    backgroundColor: theme.vars.palette.Surface.Highlight,
    content: <Result_PS_2 />,
  },
  {
    type: "result",
    seqByType: 3,
    centerFullScreen: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    content: <Result_PS_3 />,
  },
  {
    type: "result",
    seqByType: 4,
    hideBack: true,
    hideProgress: true,
    content: <Result_PS_4 />,
  },
  {
    type: "analysis",
    seqByType: 1,
    hideProgress: true,
    backgroundColor: theme.vars.palette.Background.Level1,
    content: <Analysis_PS_1 />,
  },
  {
    type: "curriculum",
    seqByType: 1,
    hideProgress: true,
    hideHeader: true,
    backgroundColor: theme.vars.palette.Background.Level3,
    content: <Curriculum_PS_1 />,
  },
  {
    type: "product",
    seqByType: 1,
    hideHeader: true,
    hideProgress: true,
    content: <></>,
  },
  {
    type: "payment",
    seqByType: 1,
    hideHeader: true,
    hideProgress: true,
    content: <></>,
  },
];

function findIndexByStepInfo(
  steps: ProgramSetupStep[],
  type: string,
  seqByType: number
): number {
  return steps.findIndex(
    (step) => step.type === type && step.seqByType === seqByType
  );
}

export default function ProgramSetupScreen_3() {
  const { type, id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useRecoilValue(userAtom);
  const close = useClose();

  const isInappWebview = useIsInappWebview();

  const steps = useMemo(() => {
    return [...commonSteps];
  }, []);

  const numId = parseInt(id || "0");
  const questionNumber = searchParams.get("q");

  const currentStepIndex = Math.min(
    Math.max(findIndexByStepInfo(steps, type!!, numId), 0),
    steps.length - 1
  ); // 유효한 범위 내로 제한

  const currentStep = steps[currentStepIndex];

  const handleBack = () => {
    if (currentStepIndex <= 0) return;
    const prevStep = steps[currentStepIndex - 1];
    navigate(`/program-setup-3/${prevStep.type}/${prevStep.seqByType}`);
  };

  const setMixPanel = useMixpanelWeb({
    eventName: "온보딩",
    params: {
      type: `${currentStep.type}`,
      pageId: `${currentStep.seqByType}`,
    },
  });

  const { mutate: savePage } = useMutation(
    ({
      setupSeq,
      pageId,
      params,
    }: {
      setupSeq: number;
      pageId: string;
      params?: string;
    }) => programSetupApi.savePage(setupSeq, pageId, params),
    {
      onSuccess: () => {},
    }
  );

  useEffect(() => {
    if (type === "load") return;

    const paramsString = searchParams.toString();
    savePage({
      setupSeq: 3,
      pageId: `${type}_${id}`,
      params: paramsString || undefined,
    });

    if (type === "curriculum") return;
    if (type === "product") return;
    if (type === "payment") return;
    if (type === "analysis") return;
    setMixPanel();
  }, [type, id, searchParams]);

  if (currentStep.type === "payment") {
    return (
      <div className="w-full h-full overflow-x-hidden">
        <PaymentContentDefault
          isFromOnboarding={true}
          enableCloseButton={true}
        />
      </div>
    );
  }

  if (currentStep.type === "product") {
    return (
      <div className="w-full h-full overflow-x-hidden">
        {<ProductContentProgramSetup />}
      </div>
    );
  }

  return currentStep.centerFullScreen === true ? (
    <FullPageWithCenterContentV2
      content={currentStep.content}
      backgroundColor={currentStep.backgroundColor}
    />
  ) : (
    <FullPageWithHeaderAndProgress
      headerTitle={currentStep.title}
      hideHeader={currentStep.hideHeader}
      hideProgress={currentStep.hideProgress}
      paddingXContent={currentStep.paddingXContent}
      body={currentStep.content}
      backgroundColor={currentStep.backgroundColor}
      disableBack={currentStep.disableBack}
      hideBack={currentStep.hideBack}
      onBack={currentStepIndex > 0 ? handleBack : undefined} // 첫 번째 단계에서는 뒤로가기 버튼 숨김
      hideClose={currentStep.hideClose}
      onClose={close}
    />
  );
}
